// PersonCardRoleBound — role-bound person card on the Details surface.
// Slate avatar (NOT brand violet) preserves the page's brand-violet density rule.
// When assigned, the card is a button that reveals a contact popover on click.
// Unassigned renders as a non-interactive div with "Not assigned" placeholder.

import { useEffect, useRef, useState } from "react";
import type { PersonReference } from "../../../types";

interface PersonCardRoleBoundProps {
  role: string;
  person: PersonReference | null | undefined;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function PersonCardRoleBound({
  role,
  person,
}: PersonCardRoleBoundProps) {
  const assigned = !!person;
  const display = assigned ? initials(person!.name) : "";
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    }

    function handleMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (
        !buttonRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isOpen]);

  const avatarEl = (
    <div
      data-testid="person-avatar"
      aria-hidden="true"
      className={`h-9 w-9 rounded-full flex items-center justify-center text-caption font-semibold ${
        assigned
          ? "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
          : "bg-slate-50 text-slate-400 dark:bg-slate-800 dark:text-slate-500 border border-dashed border-slate-300 dark:border-slate-600"
      }`}
    >
      {display}
    </div>
  );

  const textEl = (
    <div className="flex flex-col">
      <span className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight">
        {role}
      </span>
      {assigned ? (
        <span className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
          {person!.name}
        </span>
      ) : (
        <span className="text-body-sm italic text-slate-400 dark:text-slate-500">
          Not assigned
        </span>
      )}
    </div>
  );

  if (!assigned) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        {avatarEl}
        {textEl}
      </div>
    );
  }

  return (
    <span className="relative block">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-3 p-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 w-full text-left focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        {avatarEl}
        {textEl}
      </button>

      {isOpen && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={`${role} contact details`}
          className="absolute top-full left-0 mt-1 min-w-[240px] rounded-lg shadow-soft-lg dark:shadow-soft-lg-dark p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 z-50"
        >
          <p className="text-body-sm font-semibold text-slate-900 dark:text-slate-100">
            {person.name}
          </p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
            {role}
          </p>
          <div className="mt-3">
            {person.email ? (
              <a
                href={`mailto:${person.email}`}
                className="text-body-sm text-brand-600 dark:text-brand-400 hover:underline break-all"
              >
                {person.email}
              </a>
            ) : (
              <span className="text-body-sm italic text-slate-400 dark:text-slate-500">
                No email recorded
              </span>
            )}
          </div>
        </div>
      )}
    </span>
  );
}

export default PersonCardRoleBound;
